import { ExportType } from '@common/constants';
import type { PracticeArea } from '@common/enums';
import type { BillingTransaction } from '@common/types/billing';
import { getMatterTypeLabel, getPracticeAreaLabel } from '@common/util';
import { Download } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import type { ChangeEvent } from 'react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable, { type Column } from '@/components/DataTable';
import { SelectionText } from '@/components/SelectionText';
import { VALID_CURRENCY_INPUT } from '@/constants';
import { theme } from '@/theme';
import { formatBillingDate, formatBillingExportDate } from '@/utils/format';
import { getTemplateTypeLabel } from '@/utils/prospectDisplay';
import { truncate } from '@/utils/string';

interface StatusOption {
    name: string;
    value: string;
}

/** Shown instead of the client name and file number of a private firm's transaction */
const PRIVATE_LABEL = 'Private';

const STATUS_OPTIONS: StatusOption[] = [
    { name: 'All Completed Transactions', value: ExportType.ALL_COMPLETED },
    { name: 'Completed Prospects', value: ExportType.LEXO_COMPLETED },
    { name: 'Completed IDVs', value: ExportType.TREEFORT_COMPLETED },
];

function matchesPracticeAreaFilter(
    transaction: BillingTransaction,
    practiceAreaFilter: PracticeArea | 'all',
): boolean {
    if (practiceAreaFilter === 'all') return true;
    return transaction.practiceArea === practiceAreaFilter;
}

function formatCurrency(value: number): string {
    return value.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

function isIdvTransaction(transaction: BillingTransaction): boolean {
    return (
        transaction.matterType === 'IDV' ||
        transaction.templateType === 'IDV' ||
        transaction.prospectName.startsWith('IDV-')
    );
}

function canChangePrice(transaction: BillingTransaction): boolean {
    return !isIdvTransaction(transaction) || transaction.completedAt !== null;
}

interface Props {
    isLoading: boolean;
    transactions: BillingTransaction[];
    selectedStatus: ExportType;
    onSelectStatus(value: ExportType): void;
    selectedPracticeArea: PracticeArea | 'all';
    onSelectPracticeArea(value: PracticeArea | 'all'): void;
    practiceAreaOptions: readonly PracticeArea[];
    dateRangeLabel: string;
    fromDate: DateTime | null;
    toDate: DateTime | null;
    isSelectable?: boolean;
    isSuperAdmin: boolean;
    onConfirmChangePrice(params: {
        prospectId: string;
        templateType: BillingTransaction['templateType'];
        basePrice: number;
    }): Promise<void> | void;
    isChangePriceLoading?: boolean;
    page: number;
    setPage(page: number): void;
}

export default function TransactionTable({
    isLoading,
    transactions,
    selectedStatus,
    onSelectStatus,
    selectedPracticeArea,
    onSelectPracticeArea,
    practiceAreaOptions,
    dateRangeLabel,
    fromDate,
    toDate,
    isSelectable = false,
    isSuperAdmin,
    onConfirmChangePrice,
    isChangePriceLoading = false,
    page,
    setPage,
}: Props) {
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isChangePriceDialogOpen, setIsChangePriceDialogOpen] = useState(false);
    const [priceInput, setPriceInput] = useState('');
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [searchKey, setSearchKey] = useState('');

    const showChangePriceButton =
        isSuperAdmin &&
        selectedRows.length > 0 &&
        transactions
            .filter(({ transactionId }) => selectedRows.includes(transactionId))
            .every(canChangePrice);

    const allStatusOptions = isSuperAdmin
        ? [...STATUS_OPTIONS, { name: 'All Transactions', value: ExportType.ALL }]
        : STATUS_OPTIONS;

    const selectedStatusLabel =
        allStatusOptions.find((option) => option.value === selectedStatus)?.name ||
        'All Completed Transactions';

    const selectedPracticeAreaLabel =
        selectedPracticeArea === 'all'
            ? 'All Practice Areas'
            : getPracticeAreaLabel(selectedPracticeArea);

    const filteredTransactions = useMemo(() => {
        const isCompletedInRange = (completedAt: BillingTransaction['completedAt']): boolean => {
            if (completedAt === null) return false;
            if (!fromDate || !toDate) return true;
            const completed = DateTime.fromISO(
                typeof completedAt === 'string' ? completedAt : new Date(completedAt).toISOString(),
            );
            return completed >= fromDate && completed <= toDate;
        };

        return (
            transactions
                // Filter to only the billable transactions unless 'All' is selected
                .filter(
                    (tx) => selectedStatus === ExportType.ALL || isCompletedInRange(tx.completedAt),
                )
                .filter((tx) => {
                    switch (selectedStatus) {
                        case ExportType.LEXO_COMPLETED:
                            return tx.prospectName.startsWith('P-') && tx.templateType !== 'IDV';
                        case ExportType.TREEFORT_COMPLETED:
                            return tx.prospectName.startsWith('IDV-') || tx.templateType === 'IDV';
                        default:
                            return true;
                    }
                })
                .filter((tx) => matchesPracticeAreaFilter(tx, selectedPracticeArea))
                // Apply search filter
                .filter((tx) => {
                    const lowerSearch = searchKey.toLowerCase();
                    const isSearchKeyMatched =
                        searchKey === '' ||
                        tx.prospectName.toLowerCase().includes(lowerSearch) ||
                        tx.contactName?.toLowerCase().includes(lowerSearch) ||
                        tx.customFileNumber?.toLowerCase().includes(lowerSearch);
                    return isSearchKeyMatched;
                })
        );
    }, [transactions, searchKey, selectedStatus, selectedPracticeArea, fromDate, toDate]);

    const selectedRow = useMemo(
        () => filteredTransactions.find((row) => row.transactionId === selectedRows[0]) ?? null,
        [filteredTransactions, selectedRows],
    );

    const initialSort = useMemo(() => ({ key: 'completedAt', direction: 'asc' as const }), []);

    const columns: Column<BillingTransaction>[] = [
        {
            id: 'prospectName',
            label: 'Transaction',
            _isSortable: true,
            // A private firm's matter can't be opened without its support access grant
            _render: (row) =>
                row.isDeleted || row.isRedacted ? (
                    `${row.prospectName}${row.isDeleted ? ' (Archived)' : ''}`
                ) : (
                    <Link
                        style={{ fontWeight: '500' }}
                        to={
                            row.prospectName.startsWith('P-')
                                ? row.matterType === 'IDV'
                                    ? `prospects/${row.prospectId}/id-verification`
                                    : `/prospects/${row.prospectId}/summary`
                                : `/idv/${row.prospectId}/id-verification`
                        }
                    >
                        {row.prospectName}
                    </Link>
                ),
        },
        {
            id: 'customFileNumber',
            label: 'Custom File No.',
            _isSortable: true,
            _render: (row) =>
                row.isRedacted ? PRIVATE_LABEL : truncate(row.customFileNumber ?? '', 25),
            _width: '200px',
        },
        {
            id: 'matterType',
            label: 'Matter',
            _isSortable: true,
            _render: (row) => getMatterTypeLabel(row.matterType),
        },
        {
            id: 'templateType',
            label: 'Product',
            _isSortable: true,
            _render: (row) => getTemplateTypeLabel(row.templateType),
        },
        {
            id: 'contactName',
            label: 'Client name',
            _isSortable: true,
            _render: (row) => (row.isRedacted ? PRIVATE_LABEL : row.contactName),
        },
        ...(isSuperAdmin ? [{ id: 'companyName', label: 'Law Firm', _isSortable: true }] : []),
        { id: 'lawyerName', label: 'Assigned User', _isSortable: true },
        // Include prospectStatus only when All Transactions is selected
        ...(selectedStatus === ExportType.ALL
            ? [{ id: 'prospectStatus', label: 'Status', _isSortable: true }]
            : []),
        {
            id: 'createdAt',
            label: 'Created At',
            _isSortable: true,
            _render: (row) => formatBillingDate(row.createdAt),
        },
        {
            id: 'completedAt',
            label: 'Completed At',
            _isSortable: true,
            _render: (row) => formatBillingDate(row.completedAt),
        },
        {
            id: 'basePrice',
            label: 'Base Price',
            _isSortable: true,
            _render: (row) => formatCurrency(row.basePrice),
        },
    ];

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page when rows per page changes
    };

    const handleOpenChangePrice = () => {
        if (!selectedRow) return;
        setPriceInput(selectedRow.basePrice.toFixed(2));
        setIsChangePriceDialogOpen(true);
    };

    const handleCloseChangePrice = () => {
        setIsChangePriceDialogOpen(false);
    };

    const handleConfirmChangePriceInternal = async () => {
        if (!selectedRow) return;

        const parsed = Number.parseFloat(priceInput);
        const basePrice = Math.round(parsed * 100) / 100;

        if (!Number.isFinite(basePrice)) return;

        try {
            await onConfirmChangePrice({
                prospectId: selectedRow.prospectId,
                templateType: selectedRow.templateType,
                basePrice,
            });
            setIsChangePriceDialogOpen(false);
        } catch (error) {
            console.error(error);
        }
    };

    const handlePriceInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (VALID_CURRENCY_INPUT.test(value)) {
            setPriceInput(value);
        }
    };

    const handleExportCsv = () => {
        const escapeCsv = (value: string | number | null | undefined) => {
            const stringValue = value == null ? '' : String(value);
            const escaped = stringValue.replace(/"/g, '""');
            return `"${escaped}"`;
        };

        // Add isDeleted/Archived column for exports
        const exportColumns = [
            ...columns,
            {
                id: 'isDeleted',
                label: 'Archived',
            },
        ];
        const header = exportColumns.map((column) => escapeCsv(column.label)).join(',');
        const dataRows = filteredTransactions.map((row) =>
            exportColumns
                .map((column) => {
                    if (column.id === 'createdAt' || column.id === 'completedAt') {
                        return escapeCsv(formatBillingExportDate(row[column.id]));
                    }
                    if (column.id === 'matterType') {
                        return escapeCsv(getMatterTypeLabel(row.matterType));
                    }
                    if (column.id === 'templateType') {
                        return escapeCsv(getTemplateTypeLabel(row.templateType));
                    }
                    if (
                        row.isRedacted &&
                        (column.id === 'contactName' || column.id === 'customFileNumber')
                    ) {
                        return escapeCsv(PRIVATE_LABEL);
                    }
                    return escapeCsv(row[column.id]);
                })
                .join(','),
        );

        const csvContent = [header, ...dataRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const practiceAreaSuffix = selectedPracticeArea === 'all' ? '' : `-${selectedPracticeArea}`;
        link.download = `${selectedStatus.split(' ').join('_')}${practiceAreaSuffix}-${dateRangeLabel.split(' ').join('_')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Card
            sx={{
                borderRadius: 2,
                border: `1px solid ${theme.palette.neutral.N_200}`,
                boxShadow: 'none',
            }}
        >
            <CardContent>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    mb={2}
                    gap={1}
                    flexWrap="wrap"
                >
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            Transactions
                        </Typography>
                        <Typography variant="body2" color={theme.palette.neutral.N_700}>
                            {'Showing '}
                            <SelectionText label={selectedStatusLabel}></SelectionText>
                            {' in '}
                            <SelectionText label={selectedPracticeAreaLabel}></SelectionText>
                            {' for '}
                            <SelectionText label={dateRangeLabel}></SelectionText>
                        </Typography>
                    </Box>

                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        {showChangePriceButton && (
                            <Button
                                variant="outlined"
                                onClick={handleOpenChangePrice}
                                disabled={isChangePriceLoading}
                            >
                                Change Price
                            </Button>
                        )}
                        <TextField
                            sx={{
                                backgroundColor: theme.palette.neutral.N_50,
                                height: '40px',
                                boxSizing: 'border-box',
                                borderRadius: '8px',
                                color: theme.palette.neutral.gray,
                                width: '400px',
                            }}
                            placeholder="Search by transaction, file no. or client name"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            value={searchKey}
                            onChange={(ev) => setSearchKey(ev.target.value)}
                        />
                        <FormControl sx={{ minWidth: 240 }}>
                            <InputLabel id="practice-area-label">Practice Area</InputLabel>
                            <Select
                                labelId="practice-area-label"
                                value={selectedPracticeArea}
                                label="Practice Area"
                                onChange={(event) => {
                                    onSelectPracticeArea(
                                        event.target.value as PracticeArea | 'all',
                                    );
                                }}
                            >
                                <MenuItem value="all">All Practice Areas</MenuItem>
                                {practiceAreaOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {getPracticeAreaLabel(option)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 240 }}>
                            <InputLabel id="transaction-type-label">Transaction Type</InputLabel>
                            <Select
                                labelId="transaction-type-label"
                                value={selectedStatus}
                                label="Transaction Type"
                                onChange={(event) => {
                                    onSelectStatus(event.target.value as ExportType);
                                    setPage(0);
                                }}
                            >
                                {allStatusOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={handleExportCsv}
                            disabled={filteredTransactions.length === 0}
                        >
                            Export
                        </Button>
                    </Stack>
                </Stack>
                <DataTable
                    isLoading={isLoading}
                    generateKeyFrom="transactionId"
                    columns={columns}
                    rows={filteredTransactions}
                    initialSort={initialSort}
                    isSelectable={isSelectable}
                    selected={selectedRows}
                    setSelected={setSelectedRows}
                    selectionMode="single"
                    hideSelectAll
                    rowsPerPage={rowsPerPage}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    customStyles={{}}
                />

                <Dialog
                    open={isChangePriceDialogOpen}
                    onClose={handleCloseChangePrice}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle>Change Price</DialogTitle>
                    <DialogContent style={{ overflow: 'visible' }}>
                        <TextField
                            label="Price"
                            type="text"
                            value={priceInput}
                            onChange={handlePriceInputChange}
                            inputProps={{
                                pattern: '^[0-9]+(\\.[0-9]{0,2})?$',
                                inputMode: 'decimal',
                            }}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseChangePrice}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmChangePriceInternal}
                            disabled={isChangePriceLoading || priceInput.trim() === ''}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
}
